import { lazy, Suspense, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '@/components/SEO';
import GridBackground from '@/components/GridBackground';
import Navbar from '@/components/Navbar';
import PricingGroup from '@/components/pricing/PricingGroup';
import PricingAddons from '@/components/pricing/PricingAddons';
import PricingFaq from '@/components/pricing/PricingFaq';
import { pricingGroups, ui } from '@/components/pricing/pricingData';
import { useLang } from '@/lib/LanguageContext';

const offerCatalog = {
  '@context': 'https://schema.org',
  '@type': 'OfferCatalog',
  name: 'Jotunheims Services & Pricing',
  url: 'https://jotunheims.com/prices',
  itemListElement: pricingGroups.flatMap((g) => g.packs.map((p) => ({
    '@type': 'Offer',
    name: p.name.en,
    description: p.desc.en,
    priceCurrency: 'USD',
    ...(g.id === 'custom-tech'
      ? { priceSpecification: { '@type': 'PriceSpecification', minPrice: p.price, priceCurrency: 'USD' } }
      : { price: p.price })
  })))
};

const Footer = lazy(() => import('@/components/Footer'));
const WhatsAppFloatButton = lazy(() => import('@/components/WhatsAppFloatButton'));

export default function Pricing() {
  const { language } = useLang();
  const lang = language === 'es' ? 'es' : 'en';
  const tx = (o) => o[lang];
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (!id) return;
    const timer = setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 400);
    return () => clearTimeout(timer);
  }, []);
  return (
    <div className="min-h-screen bg-background text-foreground relative">
      <SEO
        fullTitle={lang === 'es' ? 'Precios y Paquetes: IA, Web y Apps | Jotunheims' : 'Pricing & Packages: AI, Web & App Development'}
        description={lang === 'es' ? 'Precios en USD para sitios web, IA a medida, apps móviles y plataformas empresariales de Jotunheims, Dubái. Cotiza por WhatsApp.' : 'Transparent USD pricing for websites, custom AI, mobile apps and enterprise platforms from Jotunheims, Dubai. Get a tailored quote on WhatsApp.'}
        path="/prices"
        structuredData={offerCatalog}
        lang={lang} />
      <GridBackground />
      <Navbar />
      <main className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pt-36 pb-16">
        <header className="text-center mb-6">
          <span className="text-xs font-medium tracking-[0.3em] uppercase text-primary">{tx(ui.eyebrow)}</span>
          <h1 className="font-inter font-black text-4xl md:text-6xl tracking-[-0.03em] mt-3 mb-5">{tx(ui.h1)}</h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">{tx(ui.note)}</p>
          <div className="w-16 h-px bg-primary/40 mx-auto mt-8" />
        </header>
        {pricingGroups.map((g, i) => <PricingGroup key={g.id} group={g} lang={lang} index={i} />)}
        <PricingAddons lang={lang} />
        <PricingFaq lang={lang} />
        <div className="mt-10 text-center">
          <h2 className="font-inter font-black text-2xl md:text-3xl mb-3">{tx(ui.customTitle)}</h2>
          <p className="text-muted-foreground mb-6">{tx(ui.customText)}</p>
          <Link to="/contact#contact-form" onClick={() => setTimeout(() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' }), 400)} className="inline-block px-8 py-4 bg-primary text-primary-foreground font-semibold text-sm tracking-wider uppercase rounded-lg hover:bg-primary/90 transition-all">
            {tx(ui.customCta)}
          </Link>
        </div>
      </main>
      <Suspense fallback={null}><Footer /></Suspense>
      <Suspense fallback={null}><WhatsAppFloatButton /></Suspense>
    </div>
  );
}